const CHANNEL = 'mfa-browser:web-extension-bridge:v1';
const REFRESH_INTERVAL = 3 * 60 * 1000;
const REQUEST_TIMEOUT = 5000;

type EncryptedDevicePayload = {
  v: number;
  iv: string;
  data: string;
};

type MfaDeviceIdentity = {
  signature: string;
  deviceId: string;
};

let refreshTimer: ReturnType<typeof window.setInterval> | undefined;
let resolvePayload: ((payload: EncryptedDevicePayload) => void) | undefined;
let rejectPayload: ((reason: string) => void) | undefined;
let requestTimer: ReturnType<typeof window.setTimeout> | undefined;
let resolveDeviceId: ((deviceId: string) => void) | undefined;
let rejectDeviceId: ((reason: string) => void) | undefined;
let deviceIdRequestTimer: ReturnType<typeof window.setTimeout> | undefined;
let onSignatureUpdated: ((signature: string) => void) | undefined;
let currentSignature: string | undefined;
let currentDeviceId: string | undefined;
let pendingRefresh: Promise<MfaDeviceIdentity> | undefined;
let messageListenerAttached = false;
let signatureUpdatedAt = 0;

export function isMfaDeviceSignature(value: string | undefined): boolean {
  if (!value) {
    return false;
  }
  try {
    const payload = JSON.parse(value) as Partial<EncryptedDevicePayload>;
    return payload.v === 1 && typeof payload.iv === 'string' && typeof payload.data === 'string';
  } catch {
    return false;
  }
}

function finishRequest(payload?: EncryptedDevicePayload, error?: string): void {
  if (requestTimer !== undefined) {
    window.clearTimeout(requestTimer);
  }
  const resolve = resolvePayload;
  const reject = rejectPayload;
  resolvePayload = undefined;
  rejectPayload = undefined;
  requestTimer = undefined;
  if (payload && resolve) {
    resolve(payload);
  } else if (reject) {
    reject(error || 'FAILED');
  }
}

function finishDeviceIdRequest(deviceId?: string, error?: string): void {
  if (deviceIdRequestTimer !== undefined) {
    window.clearTimeout(deviceIdRequestTimer);
  }
  const resolve = resolveDeviceId;
  const reject = rejectDeviceId;
  resolveDeviceId = undefined;
  rejectDeviceId = undefined;
  deviceIdRequestTimer = undefined;
  if (deviceId && resolve) {
    resolve(deviceId);
  } else if (reject) {
    reject(error || 'FAILED');
  }
}

function handleMfaExtensionMessage(event: MessageEvent): void {
  if (
    event.source !== window ||
    event.data?.channel !== CHANNEL ||
    (!resolvePayload && !resolveDeviceId)
  ) {
    return;
  }
  if (event.data.type === 'ENCRYPTED_DEVICE_PAYLOAD') {
    const payload = event.data.payload as EncryptedDevicePayload;
    if (payload?.v === 1 && typeof payload.iv === 'string' && typeof payload.data === 'string') {
      finishRequest(payload);
    } else {
      finishRequest(undefined, 'FAILED');
    }
  } else if (event.data.type === 'PUBLIC_DEVICE_ID') {
    if (typeof event.data.deviceId === 'string' && event.data.deviceId) {
      finishDeviceIdRequest(event.data.deviceId);
    } else {
      finishDeviceIdRequest(undefined, 'FAILED');
    }
  } else if (event.data.type === 'REQUEST_FAILED') {
    finishRequest(undefined, event.data.code || 'FAILED');
    finishDeviceIdRequest(undefined, event.data.code || 'FAILED');
  }
}

function addMessageListener(): void {
  if (messageListenerAttached) {
    return;
  }
  window.addEventListener('message', handleMfaExtensionMessage);
  messageListenerAttached = true;
}

function removeMessageListener(): void {
  if (!messageListenerAttached) {
    return;
  }
  window.removeEventListener('message', handleMfaExtensionMessage);
  messageListenerAttached = false;
}

function requestMfaDeviceSignature(): Promise<string> {
  return new Promise((resolve, reject) => {
    resolvePayload = (payload) => resolve(JSON.stringify(payload));
    rejectPayload = reject;
    requestTimer = window.setTimeout(() => finishRequest(undefined, 'UNAVAILABLE'), REQUEST_TIMEOUT);
    window.postMessage({ channel: CHANNEL, type: 'GET_ENCRYPTED_DEVICE_PAYLOAD' }, window.location.origin);
  });
}

function requestMfaDeviceId(): Promise<string> {
  return new Promise((resolve, reject) => {
    resolveDeviceId = resolve;
    rejectDeviceId = reject;
    deviceIdRequestTimer = window.setTimeout(
      () => finishDeviceIdRequest(undefined, 'UNAVAILABLE'),
      REQUEST_TIMEOUT,
    );
    window.postMessage({ channel: CHANNEL, type: 'GET_PUBLIC_DEVICE_ID' }, window.location.origin);
  });
}

function refreshMfaDeviceIdentity(): Promise<MfaDeviceIdentity> {
  if (pendingRefresh) {
    return pendingRefresh;
  }
  addMessageListener();
  const nextRefresh = Promise.all([requestMfaDeviceSignature(), requestMfaDeviceId()])
    .then(([signature, deviceId]) => {
      currentSignature = signature;
      currentDeviceId = deviceId;
      signatureUpdatedAt = Date.now();
      localStorage.setItem('macAddress', deviceId);
      onSignatureUpdated?.(signature);
      return { signature, deviceId };
    });
  pendingRefresh = nextRefresh;
  void nextRefresh.then(
    () => {
      if (pendingRefresh === nextRefresh) {
        pendingRefresh = undefined;
      }
    },
    () => {
      if (pendingRefresh === nextRefresh) {
        pendingRefresh = undefined;
      }
    },
  );
  return nextRefresh;
}

export function getMfaDeviceSignature(): Promise<string> {
  if (
    isMfaDeviceSignature(currentSignature) &&
    currentDeviceId &&
    Date.now() - signatureUpdatedAt < REFRESH_INTERVAL
  ) {
    return Promise.resolve(currentSignature as string);
  }
  return refreshMfaDeviceIdentity().then(({ signature }) => signature);
}

export function getMfaDeviceId(): Promise<string> {
  if (currentDeviceId && Date.now() - signatureUpdatedAt < REFRESH_INTERVAL) {
    return Promise.resolve(currentDeviceId);
  }
  return refreshMfaDeviceIdentity().then(({ deviceId }) => deviceId);
}

export function startMfaDeviceSignatureRefresh(onUpdated: (signature: string) => void): void {
  stopMfaDeviceSignatureRefresh();
  onSignatureUpdated = onUpdated;
  void refreshMfaDeviceIdentity().catch((error) => {
    if (error !== 'CANCELLED') {
      console.error('MFA 设备签文刷新失败', error);
    }
  });
  refreshTimer = window.setInterval(() => {
    void refreshMfaDeviceIdentity().catch((error) => {
      if (error !== 'CANCELLED') {
        console.error('MFA 设备签文刷新失败', error);
      }
    });
  }, REFRESH_INTERVAL);
}

export function stopMfaDeviceSignatureRefresh(): void {
  if (refreshTimer !== undefined) {
    window.clearInterval(refreshTimer);
    refreshTimer = undefined;
  }
  removeMessageListener();
  onSignatureUpdated = undefined;
  currentSignature = undefined;
  currentDeviceId = undefined;
  signatureUpdatedAt = 0;
  pendingRefresh = undefined;
  finishRequest(undefined, 'CANCELLED');
  finishDeviceIdRequest(undefined, 'CANCELLED');
}
